import { Stack, Link, Typography, Theme } from '@mui/material';
import pluralize from 'pluralize';
import { FC, ReactNode, useCallback } from 'react';
import {
  navigateToIssues,
  navigateToPipelineSummaryIssues,
  navigateToSbom,
} from '../../../common/routing/app-navigator';
import BottomDrawer from '../../../elements/BottomDrawer/BottomDrawer';
import MultiLineEllipsis from '../../../elements/MultiLineEllipsis/MultiLineEllipsis';
import { clearSelectedApplication } from '../../stores/applications-store-actions';
import { ApplicationInfo } from '../../applications-types';
import ApplicationCard from '../ApplicationCard/ApplicationCard';
import { FilterIssueType } from '../../../issues/common/types/issues-types';
import { Nullable } from 'common-types';
import { makeStyles } from 'tss-react/mui';
import { MdAccountTree, MdCampaign as Campaign } from 'react-icons/md';
import { FaInfinity } from 'react-icons/fa';
import CustomButton from '../../../elements/CustomButton/CustomButton';

const ApplicationDrawer: FC<
  React.PropsWithChildren<ApplicationDrawerProps>
> = ({
  className,
  app,
  loading,
  defaultHeight,
  onResizeStop,
  applicationIssuesScore,
  applicationStatisticas,
}) => {
  const { repoIcon: RepoIcon, appName, link } = app || {};
  const { classes } = useStyles();

  const handleNavigateToIssues = useCallback(() => {
    appName &&
      navigateToIssues({
        apps: [appName],
      });
  }, [appName]);

  const handleNavigateToSbom = useCallback(() => {
    appName &&
      navigateToSbom({
        apps: [appName],
      });
  }, [appName]);

  const handleNavigateToPipelines = useCallback(() => {
    appName &&
      navigateToPipelineSummaryIssues({
        apps: [appName],
      });
  }, [appName]);

  return (
    <BottomDrawer
      className={className}
      onClose={clearSelectedApplication}
      loading={!!loading}
      defaultHeight={defaultHeight}
      onResizeStop={onResizeStop}
      title={
        app && (
          <Stack direction='row' justifyContent='space-between'>
            <div className={classes.appName}>
              {RepoIcon && (
                <RepoIcon
                  style={{ width: 'auto', height: 15, flexShrink: 0 }}
                />
              )}
              <MultiLineEllipsis lines={2}>
                {link ? (
                  <Link href={link} target='_blank'>
                    {app.appName}
                  </Link>
                ) : (
                  app.appName
                )}
              </MultiLineEllipsis>
            </div>
            <div className={classes.buttons}>
              {app.pipelineScans && app.pipelineScans > 0 && (
                <CustomButton
                  handleClick={handleNavigateToPipelines}
                  objIcon={<FaInfinity />}>
                  <Typography>
                    {pluralize('Pipeline Scans', app.pipelineScans, true)}
                  </Typography>
                </CustomButton>
              )}
              {app.issues > 0 && (
                <CustomButton
                  handleClick={handleNavigateToIssues}
                  objIcon={<Campaign />}>
                  <Typography>
                    {pluralize('Issue', app.issues, true)}
                  </Typography>
                </CustomButton>
              )}
              {app.sbomCount > 0 && (
                <CustomButton
                  handleClick={handleNavigateToSbom}
                  objIcon={<MdAccountTree />}>
                  <Typography>{`${app.sbomCount} Dependencies (SBOM)`}</Typography>
                </CustomButton>
              )}
            </div>
          </Stack>
        )
      }>
      {app && (
        <ApplicationCard
          app={app}
          applicationIssuesScore={applicationIssuesScore}
          applicationStatisticas={applicationStatisticas}
        />
      )}
    </BottomDrawer>
  );
};

const useStyles = makeStyles()((theme: Theme) => ({
  buttons: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  appName: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
}));
export interface ApplicationDrawerProps {
  app?: ApplicationInfo;
  className: string;
  extras?: ReactNode;
  loading?: boolean;
  defaultHeight?: number;
  onResizeStop?: (height: number) => void;
  applicationIssuesScore?: {
    data: { [key: string]: number[] };
    labels: number[];
  };
  applicationStatisticas: Nullable<FilterIssueType>;
}

export default ApplicationDrawer;
